import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Descriptions, List, Spin, Tag, Typography, message } from 'antd'
import OtpAuthFlow from '../../components/OtpAuthFlow'
import { getCaptainOrder, claimOrder } from '../../api/captain'

function formatPickup(raw) {
  if (!raw) return null
  const v = String(raw).trim()
  if (/^\d{4}$/.test(v)) return `${v.slice(0, 2)}:${v.slice(2)}`
  return v || null
}

export default function CaptainOrderClaim() {
  const { orderId } = useParams()
  const [searchParams] = useSearchParams()
  const pickupTime = formatPickup(searchParams.get('pickup_at'))
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState(null)
  const [data, setData] = useState(null)
  const [claiming, setClaiming] = useState(false)
  const otpRef = useRef(null)

  async function load() {
    setLoading(true)
    try {
      const r = await getCaptainOrder(orderId)
      if (r.status === 401) {
        otpRef.current?.open()
        setState(null)
        return
      }
      if (r.status === 403) { setState('not_a_captain'); return }
      if (r.status === 404) { setState('not_found'); return }
      setState(r.body.state)
      setData(r.body)
    } catch (e) {
      console.error(e)
      message.error('تعذر تحميل الطلب')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [orderId])

  async function onClaim() {
    setClaiming(true)
    try {
      const r = await claimOrder(orderId)
      if (r.status === 409) {
        message.error('تم استلام الطلب من قبل كابتن آخر')
        await load()
        return
      }
      setState(r.body.state)
      setData(r.body)
    } catch (e) {
      console.error(e)
      message.error('تعذر استلام الطلب')
    } finally {
      setClaiming(false)
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: '0 12px' }} dir="rtl">
      <OtpAuthFlow ref={otpRef} onSuccess={() => load()} />

      {pickupTime && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={`وقت الاستلام: ${pickupTime}`} />
      )}

      {loading && <Spin />}

      {!loading && state === 'not_a_captain' && (
        <Alert type="error" showIcon message="أنت لست كابتن" description="هذه الصفحة مخصصة للكباتن فقط." />
      )}

      {!loading && state === 'not_found' && (
        <Alert type="error" showIcon message="الطلب غير موجود" />
      )}

      {!loading && state === 'cancelled' && (
        <Alert type="warning" showIcon message="الطلب ملغي" />
      )}

      {!loading && state === 'claimed_by_other' && (
        <Alert type="warning" showIcon message="الطلب مستلم" description="تم استلام هذا الطلب من قبل كابتن آخر." />
      )}

      {!loading && state === 'unclaimed' && data && (
        <Card title={`الطلب #${orderId}`}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="المتاجر">
              {(data.shopNames || []).map((s, i) => <Tag key={i}>{s}</Tag>)}
            </Descriptions.Item>
            <Descriptions.Item label="منطقة التوصيل">{data.deliveryLocationName || '-'}</Descriptions.Item>
            <Descriptions.Item label="رسوم التوصيل">{data.deliveryFee || '-'}</Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button type="primary" size="large" loading={claiming} onClick={onClaim}>
              استلم الطلب
            </Button>
          </div>
        </Card>
      )}

      {!loading && state === 'claimed_by_me' && data?.order && (
        <ClaimedDetails order={data.order} />
      )}
    </div>
  )
}

function ClaimedDetails({ order }) {
  return (
    <Card title={`الطلب #${order.id}`}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="اسم الزبون">{order.user?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="رقم الزبون">{order.user?.phone || '-'}</Descriptions.Item>
        <Descriptions.Item label="العنوان">{order.address || '-'}</Descriptions.Item>
        <Descriptions.Item label="منطقة التوصيل">{order.user?.deliveryLocation?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="إجمالي الزبون">{order.customerTotal ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="رسوم التوصيل">{order.deliveryFee ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="لاجمالي الكلي">{order.totalAfterDiscount ?? '-'}</Descriptions.Item>
        {order.notes && <Descriptions.Item label="ملاحظات">{order.notes}</Descriptions.Item>}
      </Descriptions>

      <Typography.Title level={5} style={{ marginTop: 16 }}>تفاصيل المتاجر</Typography.Title>
      {(order.subOrders || []).map((s) => (
          <Card extra={<div style={{fontWeight: 'bold'}}>مستحقات المتجر : {s.totalAfterDiscount}</div>} key={s.id} size="small" style={{ marginBottom: 8 }} title={`${s.shopNameAr || '-'}${s.pickupHour ? ` — ${s.pickupHour}` : ''}`}>
          <List
            dataSource={s.items || []}
            renderItem={(it) => (
              <List.Item>
                <List.Item.Meta title={`${it.quantity} × ${it.itemNameAr || '-'} -> ${it.rowTotal}`} description={`(${it.quantity} × ${it.unitPrice})`} />
              </List.Item>
            )}
          />
        </Card>
      ))}
    </Card>
  )
}
